import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles,
    Scan, Shield, Target, AlertTriangle, Heart, Zap, Edit3
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo25Radiografia() {
    const [amenazaCentral, setAmenazaCentral] = useState('');
    const [defensaPrincipal, setDefensaPrincipal] = useState('');
    const [queIntentaDemostrar, setQueIntentaDemostrar] = useState('');
    const [temorRaiz, setTemorRaiz] = useState('');
    const [costo, setCosto] = useState('');
    const [decisionCorrectiva, setDecisionCorrectiva] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load draft
                const draftResponse = await axios.get(`${API_URL}/api/user/draft/radiografia`, {
                    withCredentials: true
                });
                if (draftResponse.data.data) {
                    const d = draftResponse.data.data;
                    if (d.amenazaCentral) setAmenazaCentral(d.amenazaCentral);
                    if (d.defensaPrincipal) setDefensaPrincipal(d.defensaPrincipal);
                    if (d.queIntentaDemostrar) setQueIntentaDemostrar(d.queIntentaDemostrar);
                    if (d.temorRaiz) setTemorRaiz(d.temorRaiz);
                    if (d.costo) setCosto(d.costo);
                    if (d.decisionCorrectiva) setDecisionCorrectiva(d.decisionCorrectiva);
                }

                // Load preloaded data from profile if draft is empty
                const profileResponse = await axios.get(`${API_URL}/api/user/profile`, {
                    withCredentials: true
                });
                
                const summaries = profileResponse.data.ai_summaries || [];
                
                // Find relevant data from previous modules
                const amenazaData = summaries.find(s => s.module === 'mapa_amenaza');
                const contratoData = summaries.find(s => s.module === 'contrato_oculto');
                const arbolData = summaries.find(s => s.module === 'arbol_temor');
                const radarData = summaries.find(s => s.module === 'radar_exigencia');
                
                // Pre-fill if empty
                if (!draftResponse.data.data?.amenazaCentral && amenazaData?.amenaza_tipo) {
                    setAmenazaCentral(amenazaData.amenaza_tipo);
                }
                if (!draftResponse.data.data?.defensaPrincipal && radarData?.tipo_exigencia) {
                    setDefensaPrincipal(`Exigencia ${radarData.tipo_exigencia}`);
                }
                if (!draftResponse.data.data?.queIntentaDemostrar && contratoData?.que_demostrar) {
                    setQueIntentaDemostrar(contratoData.que_demostrar);
                }
                if (!draftResponse.data.data?.temorRaiz && arbolData?.categoria_temor_raiz) {
                    setTemorRaiz(arbolData.categoria_temor_raiz);
                }
                if (!draftResponse.data.data?.costo && contratoData?.costo_patron) {
                    setCosto(contratoData.costo_patron);
                }
                if (draftResponse.data.read_only) { setReadOnly(true); setSubmittedAt(draftResponse.data.submitted_at); }
            } catch (error) {
                // silent - data load failure handled gracefully
            } finally {
                setLoadingDraft(false);
            }
        };
        loadData();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { 
                    module_id: 'radiografia', 
                    data: { amenazaCentral, defensaPrincipal, queIntentaDemostrar, temorRaiz, costo, decisionCorrectiva } 
                },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [amenazaCentral, defensaPrincipal, queIntentaDemostrar, temorRaiz, costo, decisionCorrectiva]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [amenazaCentral, defensaPrincipal, queIntentaDemostrar, temorRaiz, costo, decisionCorrectiva, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && amenazaCentral.trim().length > 0 && 
                      defensaPrincipal.trim().length > 0 && 
                      queIntentaDemostrar.trim().length > 0 && 
                      temorRaiz.trim().length > 0 && 
                      costo.trim().length > 0 && 
                      decisionCorrectiva.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todos los campos. La decisión correctiva debe ser específica.');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/radiografia/submit`,
                { 
                    amenaza_central: amenazaCentral,
                    defensa_principal: defensaPrincipal,
                    que_intenta_demostrar: queIntentaDemostrar,
                    temor_raiz: temorRaiz,
                    costo,
                    decision_correctiva: decisionCorrectiva
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('¡Capítulo 2 completado!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar');
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-2xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8">
                        <div className="text-center mb-8">
                            <div className="w-24 h-24 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                                <Scan className="h-12 w-12 text-white" />
                            </div>
                            <h2 className="text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Radiografía completa</h2>
                            <p className="text-[#5FAF9D] font-medium">Capítulo 2 finalizado</p>
                        </div>
                        
                        {/* Profile Summary */}
                        <div className="bg-gradient-to-br from-[#F5F7F8] to-white rounded-xl border border-[#DCE3E8] p-6 mb-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <AlertTriangle className="h-4 w-4 text-[#E8B27A]" />
                                        <p className="text-xs text-[#7A8A93] uppercase">Amenaza central</p>
                                    </div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{amenazaCentral}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Shield className="h-4 w-4 text-[#3E5F6E]" />
                                        <p className="text-xs text-[#7A8A93] uppercase">Defensa principal</p>
                                    </div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{defensaPrincipal}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Target className="h-4 w-4 text-[#5FAF9D]" />
                                        <p className="text-xs text-[#7A8A93] uppercase">Qué intenta demostrar</p>
                                    </div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{queIntentaDemostrar}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Heart className="h-4 w-4 text-[#D07F7E]" />
                                        <p className="text-xs text-[#7A8A93] uppercase">Temor raíz</p>
                                    </div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{temorRaiz}</p>
                                </div>
                            </div>
                            <div className="p-4 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-lg border border-[#5FAF9D]/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap className="h-4 w-4 text-[#5FAF9D]" />
                                    <p className="text-xs text-[#5FAF9D] uppercase font-semibold">Decisión correctiva</p>
                                </div>
                                <p className="text-[#1F2A30] font-medium">{decisionCorrectiva}</p>
                            </div>
                        </div>
                        
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="flex justify-center">
                            <div className="mb-6"><ModuleFeedback moduleId="radiografia" chapter={2} /></div>
                            <Link to="/usuario">
                                <Button className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] hover:from-[#2F4A57] hover:to-[#1F2A30] text-white px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all text-lg">
                                    Volver al inicio
                                    <ArrowRight className="h-5 w-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 2</span>
                                    <span className="text-xs text-[#7A8A93]">•</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 2.5</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Radiografía núcleo-amenaza-defensa</h1>
                            </div>
                        </div>
                        {savingDraft && !readOnly && (
                            <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                            </span>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] p-8 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <Scan className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-white">Tu perfil integrado SENDA</CardTitle>
                                <p className="text-sm text-white/70">Revisa y confirma los datos del Capítulo 2</p>
                            </div>
                        </div>
                        <p className="mt-4 text-sm text-white/80 flex items-center gap-2">
                            <Edit3 className="h-4 w-4" />
                            Los campos vienen pre-cargados. Puedes editarlos antes de guardar.
                        </p>
                    </CardHeader>
                    
                    <CardContent className="p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Amenaza Central */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-[#E8B27A]" />
                                    Amenaza central
                                </Label>
                                <Textarea
                                    value={amenazaCentral}
                                    onChange={(e) => setAmenazaCentral(e.target.value)}
                                    placeholder="La amenaza que se activa..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="amenaza-central-input"
                                />
                            </div>

                            {/* Defensa Principal */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-[#3E5F6E]" />
                                    Defensa principal
                                </Label>
                                <Textarea
                                    value={defensaPrincipal}
                                    onChange={(e) => setDefensaPrincipal(e.target.value)}
                                    placeholder="El mecanismo de defensa..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="defensa-input"
                                />
                            </div>

                            {/* Que intenta demostrar */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <Target className="h-4 w-4 text-[#5FAF9D]" />
                                    Qué intenta demostrar
                                </Label>
                                <Textarea
                                    value={queIntentaDemostrar}
                                    onChange={(e) => setQueIntentaDemostrar(e.target.value)}
                                    placeholder="Lo que siente que debe probar..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="demostrar-input"
                                />
                            </div>

                            {/* Temor Raiz */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <Heart className="h-4 w-4 text-[#D07F7E]" />
                                    Temor raíz
                                </Label>
                                <Textarea
                                    value={temorRaiz}
                                    onChange={(e) => setTemorRaiz(e.target.value)}
                                    placeholder="El miedo profundo..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="temor-input"
                                />
                            </div>
                        </div>

                        {/* Costo */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                Costo de mantener este patrón
                            </Label>
                            <Textarea
                                value={costo}
                                onChange={(e) => setCosto(e.target.value)}
                                placeholder="¿Qué te cuesta emocional, conductual y prácticamente?"
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                data-testid="costo-input"
                            />
                        </div>

                        {/* Decision Correctiva */}
                        <div className="space-y-2 p-4 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D] flex items-center gap-2">
                                <Zap className="h-4 w-4" />
                                Decisión correctiva
                            </Label>
                            <Textarea
                                value={decisionCorrectiva}
                                onChange={(e) => setDecisionCorrectiva(e.target.value)}
                                placeholder="¿Qué acción concreta tomarás para comenzar a cambiar este patrón? Sé específico/a y realista."
                                className="border-[#5FAF9D]/30 bg-white focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[100px]"
                                data-testid="decision-input"
                            />
                            <p className="text-xs text-[#5FAF9D]">
                                La decisión correctiva debe ser específica, accionable y realizable
                            </p>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] hover:from-[#4A9A89] hover:to-[#3D8A7A] text-white px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all text-lg"
                                data-testid="submit-radiografia"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar Capítulo 2
                                        <Check className="h-5 w-5 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
